import React, { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { SUPPLIER_CATEGORIES, labelFor } from "@/lib/constants";
import IndustrySelect, { useIndustries } from "@/components/IndustrySelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, ScanLine, Layers, PencilLine, Loader2, Camera, Trash2, Download, ChevronDown, Upload, AlertTriangle, Contact } from "lucide-react";
import { saveToPhone } from "@/components/ContactsEditor";
import { toast } from "sonner";

const readBase64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(String(r.result));
  r.onerror = rej;
  r.readAsDataURL(file);
});

// Map an OCR card result into review fields, routing an explicitly-labelled
// WhatsApp number into the WhatsApp (second phone) slot.
function mapCard(data) {
  const all = (data.phones || []).map((x) => (x || "").trim()).filter(Boolean);
  const wa = (data.whatsapp || "").trim();
  const rest = all.filter((x) => x !== wa);
  return {
    name: data.company || "", contact_name: data.contact_name || "", job_title: data.job_title || "",
    email: data.email || "", website: data.website || "", address: data.address || "",
    phone: rest[0] || (wa ? "" : all[0]) || "",
    phone2: wa || rest[1] || "",
    phone3: wa ? (rest[1] || "") : (all[2] || ""),
  };
}

// Build a create-payload for a single record from review fields.
function toPayload(kind, f) {
  const contacts = (f.contact_name || f.email || f.phone || f.phone2 || f.phone3 || f.job_title)
    ? [{ name: f.contact_name || "", job_title: f.job_title || "", email: f.email || "", phone: f.phone || "", whatsapp: f.phone2 || "", phone3: f.phone3 || "", primary: true }]
    : [];
  const base = {
    name: f.name || "", contact_person: f.contact_name || "", contact_email: f.email || "",
    phone: f.phone || "", website: f.website || "", address: f.address || "",
    notes: f.notes || "", contacts, visibility: "team",
  };
  if (kind === "supplier") {
    return { ...base, industry: f.industry || "", categories: f.category ? [f.category] : [], partner_type: "supplier", supplier_type: f.origin === "local" ? "local" : "international" };
  }
  return { ...base, industry: f.industry || "mining" };
}

export const EMPTY_CONTACT = { contact_name: "", job_title: "", email: "", phone: "", phone2: "", phone3: "", website: "", address: "" };

// Contact/company fields shared by Manual Entry and Scan Business Card.
export function contactPayload(f) {
  const p = toPayload("client", { ...f, name: "" });
  return { contact_person: p.contact_person, contact_email: p.contact_email, phone: p.phone, whatsapp: f.phone2 || "", website: p.website, address: p.address, contacts: p.contacts };
}

export function ContactCardFields({ f, set, kind }) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid={`${kind}-contact-fields`}>
      <div><Label className="text-xs">Contact Name</Label><Input value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid={`${kind}-manual-contact`} /></div>
      <div><Label className="text-xs">Job Title</Label><Input value={f.job_title} onChange={(e) => set("job_title", e.target.value)} data-testid={`${kind}-manual-jobtitle`} /></div>
      <div><Label className="text-xs">Email</Label><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid={`${kind}-manual-email`} /></div>
      <div><Label className="text-xs">Phone</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid={`${kind}-manual-phone`} /></div>
      <div><Label className="text-xs">WhatsApp / Phone 2</Label><Input value={f.phone2} onChange={(e) => set("phone2", e.target.value)} data-testid={`${kind}-manual-phone2`} /></div>
      <div><Label className="text-xs">Phone 3</Label><Input value={f.phone3} onChange={(e) => set("phone3", e.target.value)} data-testid={`${kind}-manual-phone3`} /></div>
      <div className="sm:col-span-2"><Label className="text-xs">Website</Label><Input value={f.website} onChange={(e) => set("website", e.target.value)} data-testid={`${kind}-manual-website`} /></div>
      <div className="sm:col-span-2"><Label className="text-xs">Address</Label><Input value={f.address} onChange={(e) => set("address", e.target.value)} data-testid={`${kind}-manual-address`} /></div>
    </div>
  );
}

export default function CrmQuickAdd({ kind, onManual, onCreated }) {
  const label = kind === "supplier" ? "Supplier" : "Client";
  const { industries } = useIndustries();
  const catOptions = kind === "supplier"
    ? SUPPLIER_CATEGORIES.map((c) => ({ value: c.value, label: c.label }))
    : industries.map((i) => ({ value: i.value, label: i.label }));

  const [scanOpen, setScanOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button data-testid={`add-${kind}-button`}>
            <Plus className="mr-2 h-4 w-4" /><span className="sm:hidden">New</span><span className="hidden sm:inline">New {label}</span><ChevronDown className="ml-1.5 h-4 w-4 opacity-80" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onClick={onManual} data-testid={`add-${kind}-manual`}>
            <PencilLine className="mr-2 h-4 w-4" />Manual Entry
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setScanOpen(true)} data-testid={`add-${kind}-scan`}>
            <ScanLine className="mr-2 h-4 w-4" />Scan Business Card
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setBulkOpen(true)} data-testid={`add-${kind}-bulk`}>
            <Layers className="mr-2 h-4 w-4" />Bulk Add
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ScanCardDialog kind={kind} label={label} catOptions={catOptions} open={scanOpen} onOpenChange={setScanOpen} onCreated={onCreated} />
      <BulkAddDialog kind={kind} label={label} catOptions={catOptions} open={bulkOpen} onOpenChange={setBulkOpen} onCreated={onCreated} />
    </>
  );
}

const EMPTY_FIELDS = { name: "", contact_name: "", job_title: "", email: "", phone: "", phone2: "", phone3: "", website: "", address: "", notes: "", industry: "mining", category: "", origin: "international" };

function ScanCardDialog({ kind, label, catOptions, open, onOpenChange, onCreated }) {
  const fileRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState("");
  const [f, setF] = useState(EMPTY_FIELDS);
  const [scanned, setScanned] = useState(false);
  const [existing, setExisting] = useState(new Set());
  useEffect(() => {
    if (!open) return;
    api.get(`/crm/existing-names?kind=${kind}s`).then((r) => setExisting(new Set(r.data.names || []))).catch(() => {});
  }, [open, kind]);
  const isDup = f.name.trim() && existing.has(f.name.trim().toLowerCase());

  const reset = () => { setF(EMPTY_FIELDS); setPreview(""); setScanned(false); };
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Please use an image under 10MB."); return; }
    const dataUrl = await readBase64(file);
    setPreview(dataUrl);
    setScanning(true);
    try {
      const { data } = await api.post("/crm/scan-card", { file_base64: dataUrl, content_type: file.type });
      setF((pp) => ({ ...pp, ...mapCard(data) }));
      setScanned(true);
      toast.success("Card read — review and confirm the details.");
    } catch (err) { toast.error(apiError(err)); } finally { setScanning(false); }
  };

  const create = async () => {
    if (!f.name.trim()) { toast.error(`Enter the ${kind} name.`); return; }
    setSaving(true);
    try {
      await api.post(`/${kind}s`, toPayload(kind, f));
      toast.success(`${label} created`);
      onOpenChange(false); reset(); onCreated();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`${kind}-scan-dialog`}>
        <DialogHeader>
          <DialogTitle>Scan Business Card — New {label}</DialogTitle>
          <DialogDescription>Snap or upload a card photo and we'll read the details for you to confirm.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-[#FAFBFD] px-4 py-6 text-sm font-medium text-slate-600 hover:bg-slate-50" data-testid={`${kind}-scan-upload`}>
            {scanning ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
            {scanning ? "Reading card…" : preview ? "Use a different photo" : "Take photo or upload card image"}
            <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment" className="hidden" onChange={onFile} data-testid={`${kind}-scan-input`} />
          </label>
          {scanning && <div className="flex items-center justify-center gap-2 text-xs font-medium text-[#1B4383]" data-testid={`${kind}-scan-loading`}><Loader2 className="h-3.5 w-3.5 animate-spin" />Reading the card with AI… this can take a few seconds.</div>}
          {preview && !preview.startsWith("data:application") && (
            <img src={preview} alt="Card preview" className="mx-auto max-h-32 rounded-md border border-border object-contain" />
          )}

          {(scanned || preview) && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2"><Label className="text-xs">{label} / Company Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid={`${kind}-scan-name`} />{isDup && <div className="mt-1 flex items-center gap-1 text-[11px] font-medium text-[#A87F31]" data-testid={`${kind}-scan-dup-warning`}><AlertTriangle className="h-3.5 w-3.5" />A {kind} named &ldquo;{f.name.trim()}&rdquo; already exists in the CRM.</div>}</div>
              <div><Label className="text-xs">Contact Name</Label><Input value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid={`${kind}-scan-contact`} /></div>
              <div><Label className="text-xs">Job Title</Label><Input value={f.job_title} onChange={(e) => set("job_title", e.target.value)} /></div>
              <div><Label className="text-xs">Email</Label><Input value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
              <div><Label className="text-xs">Phone</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid={`${kind}-scan-phone`} /></div>
              <div><Label className="text-xs">WhatsApp / Phone 2</Label><Input value={f.phone2} onChange={(e) => set("phone2", e.target.value)} data-testid={`${kind}-scan-phone2`} /></div>
              <div><Label className="text-xs">Phone 3</Label><Input value={f.phone3} onChange={(e) => set("phone3", e.target.value)} data-testid={`${kind}-scan-phone3`} /></div>
              <div><Label className="text-xs">Website</Label><Input value={f.website} onChange={(e) => set("website", e.target.value)} /></div>
              <div>
                <Label className="text-xs">{kind === "supplier" ? "Category" : "Industry"}</Label>
                <Select value={kind === "supplier" ? f.category : f.industry} onValueChange={(v) => set(kind === "supplier" ? "category" : "industry", v)}>
                  <SelectTrigger className="h-9 text-sm" data-testid={`${kind}-scan-cat`}><SelectValue placeholder="Choose" /></SelectTrigger>
                  <SelectContent>{catOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {kind === "supplier" && (
                <div>
                  <Label className="text-xs">Industry</Label>
                  <IndustrySelect value={f.industry} onChange={(v) => set("industry", v)} testid={`${kind}-scan-industry`} />
                </div>
              )}
              {kind === "supplier" && (
                <div>
                  <Label className="text-xs">Origin</Label>
                  <div className="inline-flex overflow-hidden rounded-md border border-border">
                    {[["local", "Local"], ["international", "International"]].map(([v, l]) => (
                      <button key={v} type="button" onClick={() => set("origin", v)} className={`px-3 py-1.5 text-xs font-medium ${f.origin === v ? "bg-[#1B4383] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{l}</button>
                    ))}
                  </div>
                </div>
              )}
              <div className="sm:col-span-2"><Label className="text-xs">Address</Label><Input value={f.address} onChange={(e) => set("address", e.target.value)} /></div>
            </div>
          )}
        </div>

        <DialogFooter className="sticky bottom-0 -mx-6 -mb-6 border-t border-border bg-background px-6 py-3 sm:static sm:mx-0 sm:mb-0 sm:border-0 sm:px-0 sm:py-0">
          {scanned && (f.contact_name || f.phone || f.phone2 || f.email) && (
            <Button variant="outline" onClick={() => saveToPhone({ name: f.contact_name || f.name, job_title: f.job_title, email: f.email, phone: f.phone, whatsapp: f.phone2, phone3: f.phone3 }, f.name)} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 sm:mr-auto" data-testid={`${kind}-scan-save-phone`}><Contact className="mr-1.5 h-4 w-4" />Save To Phone</Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={create} disabled={saving || !f.name.trim()} data-testid={`${kind}-scan-create`} className="bg-[#1B4383] hover:bg-[#16386e]">
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}Create {label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkAddDialog({ kind, label, catOptions, open, onOpenChange, onCreated }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [existing, setExisting] = useState(new Set());
  useEffect(() => {
    if (!open) return;
    api.get(`/crm/existing-names?kind=${kind}s`).then((r) => setExisting(new Set(r.data.names || []))).catch(() => {});
  }, [open, kind]);
  const isDupName = (n) => n && existing.has(n.trim().toLowerCase());
  const dupCount = rows.filter((r) => isDupName(r.name)).length;

  const reset = () => { setRows([]); setProgress(""); };
  const setRow = (i, k, v) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const removeRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    const collected = [];
    for (let i = 0; i < files.length; i++) {
      setProgress(`Reading card ${i + 1} of ${files.length}…`);
      try {
        const dataUrl = await readBase64(files[i]);
        const { data } = await api.post("/crm/scan-card", { file_base64: dataUrl, content_type: files[i].type });
        collected.push({ ...EMPTY_FIELDS, ...mapCard(data) });
      } catch { /* skip unreadable card */ }
    }
    setRows((rs) => [...rs, ...collected]);
    setBusy(false); setProgress("");
    e.target.value = "";
    toast.success(`Read ${collected.length} of ${files.length} card${files.length !== 1 ? "s" : ""}`);
  };

  const onExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setProgress("Parsing spreadsheet…");
    try {
      const dataUrl = await readBase64(file);
      const { data } = await api.post("/crm/parse-import", { kind: `${kind}s`, file_base64: dataUrl });
      const parsed = (data.rows || []).map((r) => ({ ...EMPTY_FIELDS, ...r }));
      setRows((rs) => [...rs, ...parsed]);
      toast.success(`Loaded ${parsed.length} row${parsed.length !== 1 ? "s" : ""} from the spreadsheet`);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); setProgress(""); e.target.value = ""; }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/crm/import-template?kind=${kind}s`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `somcore_${kind}s_import_template.xlsx`; a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Could not download template"); }
  };

  const createAll = async () => {
    const valid = rows.filter((r) => (r.name || "").trim());
    if (!valid.length) { toast.error("Add at least one row with a name."); return; }
    setBusy(true);
    try {
      const records = valid.map((r) => toPayload(kind, r));
      const { data } = await api.post(`/${kind}s/bulk-create`, { records });
      toast.success(`Created ${data.created} ${label.toLowerCase()}${data.created !== 1 ? "s" : ""}`);
      onOpenChange(false); reset(); onCreated();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid={`${kind}-bulk-dialog`}>
        <DialogHeader>
          <DialogTitle>Bulk Add {label}s</DialogTitle>
          <DialogDescription>Scan several business cards or import a spreadsheet, review, then create them all.</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="photos">
          <TabsList data-testid={`${kind}-bulk-tabs`}>
            <TabsTrigger value="photos" data-testid={`${kind}-bulk-tab-photos`}>Multiple Photos</TabsTrigger>
            <TabsTrigger value="excel" data-testid={`${kind}-bulk-tab-excel`}>Excel Import</TabsTrigger>
          </TabsList>
          <TabsContent value="photos" className="pt-3">
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-[#FAFBFD] px-4 py-5 text-sm font-medium text-slate-600 hover:bg-slate-50" data-testid={`${kind}-bulk-photos-label`}>
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
              {busy ? (progress || "Working…") : "Select Multiple Card Photos"}
              <input type="file" accept="image/*" multiple className="hidden" onChange={onPhotos} data-testid={`${kind}-bulk-photos-input`} />
            </label>
          </TabsContent>
          <TabsContent value="excel" className="pt-3">
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid={`${kind}-bulk-template`}><Download className="mr-1.5 h-4 w-4" />Download Template</Button>
              <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-input bg-white px-3 py-1.5 text-sm font-medium hover:bg-slate-50" data-testid={`${kind}-bulk-excel-label`}>
                <Upload className="h-4 w-4" />{busy ? (progress || "Working…") : "Upload Filled .xlsx"}
                <input type="file" accept=".xlsx" className="hidden" onChange={onExcel} data-testid={`${kind}-bulk-excel-input`} />
              </label>
            </div>
          </TabsContent>
        </Tabs>

        {dupCount > 0 && (
          <div className="mt-2 flex items-center gap-1.5 rounded-md bg-[#FBF6E9] px-3 py-2 text-xs font-medium text-[#8A6D1F]" data-testid={`${kind}-bulk-dup-banner`}>
            <AlertTriangle className="h-4 w-4 shrink-0" />{dupCount} of these already exist in the CRM (highlighted below) — remove or rename them before creating.
          </div>
        )}
        {rows.length > 0 && (
          <div className="mt-2 max-h-[46vh] overflow-auto rounded-lg border border-border" data-testid={`${kind}-bulk-review`}>
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50 text-left text-slate-500">
                <tr>
                  <th className="px-2 py-2 font-medium">Name</th>
                  <th className="px-2 py-2 font-medium">Contact</th>
                  <th className="px-2 py-2 font-medium">Email</th>
                  <th className="px-2 py-2 font-medium">Phone</th>
                  <th className="px-2 py-2 font-medium">{kind === "supplier" ? "Category" : "Industry"}</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const dup = isDupName(r.name);
                  return (
                  <tr key={i} className={`border-t border-border ${dup ? "bg-[#FBF6E9]" : ""}`} data-testid={`${kind}-bulk-row-${i}`}>
                    <td className="px-2 py-1">
                      <Input value={r.name} onChange={(e) => setRow(i, "name", e.target.value)} className="h-8 text-xs" data-testid={`${kind}-bulk-name-${i}`} />
                      {dup && <div className="mt-0.5 flex items-center gap-1 text-[10px] font-medium text-[#A87F31]"><AlertTriangle className="h-3 w-3" />Already in CRM</div>}
                    </td>
                    <td className="px-2 py-1"><Input value={r.contact_name} onChange={(e) => setRow(i, "contact_name", e.target.value)} className="h-8 text-xs" /></td>
                    <td className="px-2 py-1"><Input value={r.email} onChange={(e) => setRow(i, "email", e.target.value)} className="h-8 text-xs" /></td>
                    <td className="px-2 py-1"><Input value={r.phone} onChange={(e) => setRow(i, "phone", e.target.value)} className="h-8 text-xs" /></td>
                    <td className="px-2 py-1">
                      <Select value={kind === "supplier" ? r.category : r.industry} onValueChange={(v) => setRow(i, kind === "supplier" ? "category" : "industry", v)}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>{catOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-1 py-1 text-right"><button onClick={() => removeRow(i)} className="text-slate-400 hover:text-destructive" data-testid={`${kind}-bulk-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button></td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <DialogFooter>
          <span className="mr-auto text-xs text-muted-foreground">{rows.length} row{rows.length !== 1 ? "s" : ""} ready</span>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={createAll} disabled={busy || !rows.length} data-testid={`${kind}-bulk-create-all`} className="bg-[#1B4383] hover:bg-[#16386e]">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}Create All ({rows.filter((r) => (r.name || "").trim()).length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const labelForCat = (v) => labelFor(v, SUPPLIER_CATEGORIES);
